import { MicrophoneStatus } from './MicrophoneStatus';
import { isStringEmpty } from '@shared/utils';
import VoiceTranslater from '@/common/voice';

import { useCallback, useEffect, useRef, useState } from 'react';
import BaseModal from '../modal';
import MicrophoneIndicator from './MicrophoneIndicator';

const Messages = {
  [MicrophoneStatus.Normal]: '请稍等...',
  [MicrophoneStatus.Preflight]: '请允许 AI 助手使用你的麦克风',
  [MicrophoneStatus.Listensing]: '请开始说话',
  [MicrophoneStatus.NotAllowed]: '未获取到麦克风使用权限',
  [MicrophoneStatus.NotFound]: '没有找到麦克风设备',
  [MicrophoneStatus.Invalid]: '未检测到语音，请检查麦克风设置',
  [MicrophoneStatus.Error]: '语音服务错误，请重试',
  [MicrophoneStatus.NotSupported]: '当前页面不支持语音识别',
};

function Content(props) {
  const { onOk } = props;

  const preflightTimerRef = useRef(-1);
  const speechRecognizerRef = useRef(null);

  const [status, setStatus] = useState(MicrophoneStatus.Normal);
  const [speechingText, setSpeechingText] = useState('');

  const handleStop = useCallback(() => {
    if (speechRecognizerRef.current) {
      speechRecognizerRef.current.stop();
      speechRecognizerRef.current = null;
    }
  }, []);

  const clearPreflightTimer = useCallback(() => {
    if (preflightTimerRef.current > 0) {
      window.clearTimeout(preflightTimerRef.current);
      preflightTimerRef.current = -1;
    }
  }, []);

  useEffect(() => {
    if (
      ![MicrophoneStatus.Speeching, MicrophoneStatus.Ended].includes(status)
    ) {
      setSpeechingText('');
    }
  }, [status]);

  const setUpASR = useCallback(() => {
    speechRecognizerRef.current = new VoiceTranslater({
      timeout: 8000,
      onWillStatusChange: (_, nextStatus) => {
        if (nextStatus === 'preflight') {
          if (status === MicrophoneStatus.Normal) {
            preflightTimerRef.current = window.setTimeout(() => {
              setStatus(MicrophoneStatus.Preflight);
            }, 300);
          }
        } else if (nextStatus === 'init') {
          clearPreflightTimer();
        } else if (nextStatus == 'runing') {
          setStatus(MicrophoneStatus.Listensing);
        }
      },
      onRecognizing: text => {
        setStatus(MicrophoneStatus.Speeching);
        setSpeechingText(text);
      },
      onRecognized: text => {
        setSpeechingText(text);
      },
      onEnded: text => {
        if (isStringEmpty(text)) {
          setStatus(MicrophoneStatus.Invalid);
          return;
        }
        setStatus(MicrophoneStatus.Ended);
        setSpeechingText(text);
        onOk(text);
      },
      onError: error => {
        clearPreflightTimer();
        const { name, message } = error;
        if (name.toLocaleLowerCase() === 'notallowederror') {
          setStatus(MicrophoneStatus.NotAllowed);
        } else if (name.toLocaleLowerCase() === 'notfounderror') {
          setStatus(MicrophoneStatus.NotFound);
        } else {
          //   if (!isStringEmpty(message)) {
          //     Message.error(message);
          //   }
          setStatus(MicrophoneStatus.Error);
        }
      },
    });
  }, []);

  useEffect(() => {
    setUpASR();
    speechRecognizerRef.current.start();
    return () => {
      handleStop();
    };
  }, []);

  return (
    <div
      className={[
        'select-none',
        'w-[720px] h-[520px] pt-[100px] pl-[32px] pr-[30px] pb-[66px] max-w-80vw',
        'flex flex-col items-center gap-[16px]',
      ]}
    >
      <section className="flex-1 flex flex-col items-center gap-[16px] overflow-hidden">
        <p
          className={[
            'mb-0',
            'max-h-full overflow-y-scroll nw-scrollbar-thin',
            'flex flex-col-reverse',
            'text-[32px] text-primary leading-normal text-center',
            {
              'opacity-40': [
                MicrophoneStatus.Normal,
                MicrophoneStatus.Listensing,
              ].includes(status),
              '!text-left': speechingText.length > 0,
              'text-[28px]': speechingText.length >= 150,
            },
          ]}
        >
          {speechingText || Messages[status]}
        </p>
        {[MicrophoneStatus.Invalid].includes(status) && (
          <a
            className={[
              'cursor-pointer',
              'text-[16px] text-[#006BFF] hover:text-[#3389FF] leading-normal !underline',
            ]}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              speechRecognizerRef.current.start();
            }}
          >
            重新尝试
          </a>
        )}
      </section>
      <MicrophoneIndicator
        active={[
          MicrophoneStatus.Listensing,
          MicrophoneStatus.Speeching,
        ].includes(status)}
      />
    </div>
  );
}

const ASRModal = {
  self: null,
  open(data) {
    return new Promise(resolve => {
      const handleOk = input => {
        resolve(input);
        this.close();
      };

      this.self = BaseModal.show({
        maskClosable: false,
        content: (
          <Content
            {...data}
            onOk={input => handleOk(input)}
            onClose={() => this.close()}
          />
        ),
      });
    });
  },
  close() {
    if (this.self) {
      this.self.destroy();
      this.self = null;
    }
  },
};

export default ASRModal;
